/**
 * Converts a base64 or URL-encoded SVG data URL into an SVG string.
 *
 * This function takes a data URL containing SVG content and decodes it into a usable SVG string.
 * It supports both base64-encoded and URL-encoded SVG data URLs, as well as plain base64 strings.
 * The function properly handles Unicode characters (Hebrew, Arabic, Chinese, etc.) by using UTF-8 decoding.
 *
 * @param dataUrl - The data URL containing the SVG content or a plain base64 string.
 * @returns The decoded SVG string if successful, or `null` if the input is invalid or decoding fails.
 *
 * @example
 * // Example with a base64-encoded SVG data URL
 * const base64DataUrl = "data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSIxMDAiIGhlaWdodD0iMTAwIj48Y2lyY2xlIGN4PSI1MCIgY3k9IjUwIiByPSI1MCIgZmlsbD0icmVkIiAvPjwvc3ZnPg==";
 * const svgString = base64UrlToSVGString(base64DataUrl);
 * console.log(svgString);
 * // Output: <svg xmlns="http://www.w3.org/2000/svg" width="100" height="100"><circle cx="50" cy="50" r="50" fill="red" /></svg>
 *
 * @example
 * // Example with a URL-encoded SVG data URL
 * const urlEncodedDataUrl = "data:image/svg+xml,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20width%3D%22100%22%20height%3D%22100%22%3E%3Ccircle%20cx%3D%2250%22%20cy%3D%2250%22%20r%3D%2250%22%20fill%3D%22red%22%20%2F%3E%3C%2Fsvg%3E";
 * const svgString = base64UrlToSVGString(urlEncodedDataUrl);
 * console.log(svgString);
 * // Output: <svg xmlns="http://www.w3.org/2000/svg" width="100" height="100"><circle cx="50" cy="50" r="50" fill="red" /></svg>
 *
 * @example
 * // Example with a plain base64 string
 * const base64String = "PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSIxMDAiIGhlaWdodD0iMTAwIj48Y2lyY2xlIGN4PSI1MCIgY3k9IjUwIiByPSI1MCIgZmlsbD0icmVkIiAvPjwvc3ZnPg==";
 * const svgString = base64UrlToSVGString(base64String);
 * console.log(svgString);
 * // Output: <svg xmlns="http://www.w3.org/2000/svg" width="100" height="100"><circle cx="50" cy="50" r="50" fill="red" /></svg>
 */
export function base64UrlToSVGString(dataUrl?: string): string | null {
  // Check if the input is a valid string
  if (typeof dataUrl !== "string") {
    return null; // Invalid input
  }
  // Trim whitespace from the input string
  dataUrl = dataUrl.trim();
  // Check if the input is empty
  if (dataUrl.length === 0) {
    return null; // Empty string
  }

  let encodedData = dataUrl;
  let isBase64Encoded = true;

  // Check if the input is a data URL
  if (dataUrl.startsWith("data:image/svg+xml")) {
    // Split the data URL into metadata and encoded content
    const [metadata, ...dataParts] = dataUrl.split(",");
    if (!metadata || dataParts.length === 0) {
      return null; // Invalid data URL format
    }

    encodedData = dataParts.join(",");
    isBase64Encoded = metadata.includes("base64");
  }

  try {
    // Decode based on the encoding type
    if (isBase64Encoded) {
      // Decode base64 to binary string
      const binaryString = atob(encodedData);

      // Convert binary string back to UTF-8 bytes
      const utf8Bytes = new Uint8Array(binaryString.length);
      for (let i = 0; i < binaryString.length; i++) {
        utf8Bytes[i] = binaryString.charCodeAt(i);
      }

      // Use TextDecoder to properly handle Unicode characters
      const decoder = new TextDecoder("utf-8");
      return decoder.decode(utf8Bytes);
    } else {
      // Decode URL-encoded data and replace single quotes with double quotes
      return decodeURIComponent(encodedData).replace(/'/g, '"');
    }
  } catch (error) {
    console.error("Error decoding SVG data URL:", error);
    return null; // Return null if decoding fails
  }
}
